import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import {
  CandidateProfileFormValue,
  NavItemType,
  Props
} from './candidateProfile.type';
import { Col, Nav, Row, Tab } from 'react-bootstrap';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { navItems, tab, TabValues, validateTab } from './config';
import * as Yup from 'yup';
import CandidateBasicForm from './form/CandidateBasicForm/CandidateBasicForm';
import CandidateSkillForm from './form/CandidateSkillForm/CandidateSkillForm';
import CandidateEducationForm from './form/CandidateEducationForm/CandidateEducationForm';
import CandidateExperienceForm from './form/CandidateExperienceForm/CandidateExperienceForm';
import CandidateRoleForm from './form/CandidateRoleForm/CandidateRoleForm';
import CandidateAvailabilityForm from './form/CandidateAvailabilityForm/CandidateAvailabilityForm';
import { useSelector } from 'react-redux';
import { RootState } from 'store/rootReducer';
import { transformInputDate } from 'helpers/utils';
import { useUpdateCandidateProfileMutation } from 'service/authQuery/authQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';

const NavItem = ({
  item,
  isError
}: {
  item: NavItemType;
  isError?: boolean;
}) => {
  return (
    <Nav.Link
      eventKey={item.eventKey}
      className={classNames(
        'text-center text-sm-start cursor-pointer outline-none d-sm-flex align-items-sm-center border-bottom-sm border-end border-end-sm-0'
      )}
    >
      <FeatherIcon
        icon={item.icon}
        size={16}
        className={classNames('me-sm-2 nav-icons', {
          'text-danger': isError
        })}
      />
      <span
        className={classNames('d-none d-sm-inline', {
          'text-danger': isError
        })}
      >
        {item.label}
      </span>
    </Nav.Link>
  );
};

const validationSchema = Yup.object({
  username: Yup.string().required('Username is required'),
  contactNumber: Yup.string()
    .matches(/^[0-9]+$/, 'Contact number must contain only digits')
    .min(10, 'Contact number must be at least 10 digits')
    .max(15, 'Contact number cannot be longer than 15 digits')
    .required('Contact number is required'),
  country: Yup.string().required('Country is required'),
  about: Yup.string()
    .max(500, 'About section cannot be longer than 500 characters')
    .optional(),
  gender: Yup.string()
    .required('Gender is required')
    .oneOf(
      ['male', 'female', 'other'],
      'Gender must be Male, Female, or Other'
    ),
  dateOfBirth: Yup.date()
    .required('Date of Birth is required')
    .max(new Date(), 'Date of Birth cannot be in the future'),
  skills: Yup.array()
    .of(
      Yup.object({
        skill: Yup.string().required('Skill is required'),
        experience: Yup.string().required('Experience is required')
      })
    )
    .min(1, 'At least one skill is required'),
  education: Yup.array().of(
    Yup.object().shape({
      level: Yup.string().required('Education level is required'),
      degree: Yup.string().required('Degree is required'),
      college: Yup.string().required('College is required'),
      startDate: Yup.date()
        .required('Start date is required')
        .typeError('Invalid start date'),
      endDate: Yup.date()
        .required('End date is required')
        .typeError('Invalid end date')
        .min(Yup.ref('startDate'), 'End date cannot be earlier than start date')
    })
  ),
  yearsOfExperience: Yup.number()
    .typeError('Years of experience must be a number')
    .min(0, 'Years of experience cannot be negative')
    .integer('Years of experience must be an integer')
    .notRequired(),
  role: Yup.string().required('Role is required'),
  currentAnnualEarning: Yup.number()
    .typeError('Current annual earning must be a valid number')
    .min(0, 'Current annual earning cannot be negative')
    .notRequired(),
  expectedAnnualEarning: Yup.number()
    .typeError('Expected annual earning must be a valid number')
    .min(0, 'Expected annual earning cannot be negative')
    .notRequired(),
  jobPreference: Yup.string().required('Job preference is required'),
  startWork: Yup.number()
    .typeError('Start work must be a number')
    .min(0, 'Start work cannot be negative')
    .integer('Start work must be an integer')
    .required('Start work option is required')
});

function CandidateProfile({ innerRef }: Props) {
  const { info, username } = useSelector((state: RootState) => state.user.user);
  const [updateProfile] = useUpdateCandidateProfileMutation();
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const getSearchValue = URLSearchParams.get('value');

  const handleSubmit = async (values: CandidateProfileFormValue) => {
    try {
      await updateProfile(values);
      navigate(-1);
    } catch (error) {
      console.log(error);
    }
  };

  const education = useMemo(() => {
    return Array.isArray(info.education) && info.education.length > 0
      ? info.education.map(item => ({
          ...item,
          endDate: transformInputDate(item.endDate ?? ''),
          startDate: transformInputDate(item.startDate ?? '')
        }))
      : [];
  }, [info.education]);

  return (
    <Formik
      innerRef={innerRef}
      initialValues={{
        username: username ?? '',
        contactNumber: info.contactNumber ?? '',
        country: typeof info.address === 'object' ? info.address.country : '',
        about: info.about ?? '',
        gender: info.gender ?? '',
        dateOfBirth: transformInputDate(info.dateOfBirth ?? '') ?? '',
        github: info.portal?.github ?? '',
        linkedIn: info.portal?.linkedIn ?? '',
        skills: info.skills ?? [{ skill: '', experience: '' }],
        education: education,
        yearsOfExperience: info.experience?.overallYear ?? '',
        role: info.role?._id,
        currentAnnualEarning: info.earning?.currentAnnualEarning ?? '',
        expectedAnnualEarning: info.earning?.expectedAnnualEarning ?? '',
        jobPreference: info.jobAvailability?.preference ?? '',
        startWork: info.jobAvailability?.startWork ?? ''
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched }) => {
        const errorFields = Object.keys(errors);
        const touchedFields = Object.keys(touched);
        const tabStatus: Record<TabValues, boolean> = validateTab(
          errorFields,
          touchedFields
        );
        return (
          <Form>
            <Tab.Container
              id="left-tabs-example"
              defaultActiveKey={getSearchValue || 'basic'}
            >
              <Row className="g-0 border-top">
                <Col xs={12} sm={4}>
                  <Nav className="flex-sm-column border-bottom border-bottom-sm-0 border-end-sm fs-9 vertical-tab justify-content-between">
                    {navItems.map(item => (
                      <NavItem
                        key={item.label}
                        item={item}
                        isError={tabStatus[item.eventKey]}
                      />
                    ))}
                  </Nav>
                </Col>
                <Col xs={12} sm={8}>
                  <Tab.Content className="py-3 ps-sm-4 h-100">
                    <Tab.Pane eventKey={tab.basic} className="h-100">
                      <h5 className="mb-3 text-body-highlight">
                        Basic Information
                      </h5>
                      <CandidateBasicForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.skill} className="h-100">
                      <CandidateSkillForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.education} className="h-100">
                      <CandidateEducationForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.experience} className="h-100">
                      <h5 className="mb-3 text-body-highlight">Experience</h5>
                      <CandidateExperienceForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.role} className="h-100">
                      <h5 className="mb-3 text-body-highlight">
                        Role and Current Earnings
                      </h5>
                      <CandidateRoleForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.availability} className="h-100">
                      <h5 className="mb-3 text-body-highlight">Availability</h5>
                      <CandidateAvailabilityForm />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tab.resume} className="h-100">
                      <h5 className="mb-3 text-body-highlight">
                        Upload Resume
                      </h5>
                    </Tab.Pane>
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CandidateProfile;
